import type { PointerEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { css, Theme } from '@emotion/react';
import { ArrowRight, DotsThreeVertical, HardDrives, PushPin } from '@phosphor-icons/react';
import type { DeploymentPinResponse } from 'api/deployment/handlers/deploymentPinHandler';
import type { ProjectDeploymentsDeployment } from 'api/project/routes/projectDeploymentsRoute';
import { DeploymentState } from 'common/CommonConstants';
import { toTitleCase, createVSCodeUri } from 'common/commonUtil';
import { CoreInstanceTypeTexts } from 'common/core/CoreInstanceConstants';
import { DeploymentPinRequest } from 'common/deployment/DeploymentApiRoutesDocuments';
import { StatusItemState } from 'common/status/StatusDocument';
import Api from 'client/lib/Api';
import { trpc } from 'client/lib/trpc';
import { useDeploymentStatusTree } from 'client/state/useDeploymentStatusTree';
import { EnabledToggleButton } from 'client/pages/deployment/components/EnabledToggleButton';
import { getDeploymentStageIcon } from 'client/ClientConstants';
import { Button } from 'client/components/Button';
import { Input } from 'client/components/Input';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { ListItemText } from 'client/components/Other/ListItemText';
import { Spectrum } from 'client/components/Spectrum';
import { getFeaturesFromState, useStatusColorToHex } from 'client/components/Status/statusUtils';
import { Tag } from 'client/components/Tag';
import { Transition, borderRadius, applyEffect, Font } from 'client/Theme';

const pinDeployment = (deploymentID: number) =>
  new Promise<void>((resolve, reject) =>
    Api.post<DeploymentPinRequest, DeploymentPinResponse>('/deployment/pin', { deploymentID })
      .then(({ status }) => {
        if (status !== 'ok') return reject(status);
        return resolve();
      })
      .catch((error) => {
        console.error(error);
        return reject('unexpected error');
      })
  );

export interface DeploymentItemProps {
  index: number;
  deployment: ProjectDeploymentsDeployment;
  link: string;
  pin: boolean;
  onMenu: (event: PointerEvent<HTMLButtonElement>) => void;
}

export const DeploymentItem = ({ index, deployment, link, pin, onMenu }: DeploymentItemProps) => {
  const queryClient = useQueryClient();
  const statusTree = useDeploymentStatusTree(deployment.id);
  const utils = trpc.useUtils();

  const deploymentStateID = statusTree ? statusTree.deploymentStateID : DeploymentState.STOPPED;
  const itemStateID = statusTree?.itemStateID || StatusItemState.GRAY;
  const coreStateID = statusTree?.core.coreStateID || null;

  const pinMutation = useMutation({
    mutationFn: pinDeployment,
    onSuccess: () => {
      utils.project.deployments.invalidate();
      queryClient.invalidateQueries(['dashboard']);
    },
  });

  const { stage, hosts } = deployment;
  const primaryHttpHost = hosts[0] || null;
  const isHttpHostEnabled = !!primaryHttpHost;
  const primaryHttpHostUrl = isHttpHostEnabled
    ? `https://${primaryHttpHost.customDomain || primaryHttpHost.tweakedDomain || primaryHttpHost.defaultDomain || ''}`
    : '';
  const primaryHttpHostName = toTitleCase(isHttpHostEnabled ? primaryHttpHost.identifier : 'address');

  const icon = getDeploymentStageIcon(deployment.stage);

  const combinedState = { deploymentStateID, itemStateID, coreStateID };
  const stateFeatures = getFeaturesFromState(combinedState);
  const stateColors = useStatusColorToHex();
  const statusBarColor = stateColors[stateFeatures.color];

  const sshUrl =
    deployment.sshUsernames.find((s) => s.namespaceName == '' && s.podName == 'dev' && s.containerName == '')?.sshConnectString || '--';
  const sshEditorPath = '/app';
  const sshConnectString = `ssh ` + sshUrl;

  const handlePin = () => pinMutation.mutateAsync(deployment.id);

  // This means that the deployment should be deleted
  // TODO: DISABLED THIS FOR NEXT UPDATE, POSSIBLE NOW TO HAVE A CORE WITH STATUS TREE
  //if (!deploymentStatusTree) return null;
  if (statusTree?.deploymentStateID == DeploymentState.DELETED) return null;

  return (
    <article css={containerStyle}>
      <header>
        <Link to={link} css={titleStyle}>
          <figure>
            <ListItemFigure icon={<icon.type size={36} weight="duotone" animate={stateFeatures.isEnabled} />} state={combinedState} />
          </figure>

          <div css={textStyle}>
            <div css={statusBarStyle} style={{ backgroundColor: statusBarColor }} />
            <ListItemText
              primary={deployment.name}
              secondary={
                <>
                  <Tag css={sizeStyle}>{CoreInstanceTypeTexts[deployment.coreInstanceTypeID]}</Tag> <HardDrives /> Deployment ·{' '}
                  {<icon.type size={14} weight="regular" />} {stage || 'Unknown'}
                </>
              }
              css={subtitleStyle}
            />
          </div>

          <ArrowRight />
        </Link>

        <EnabledToggleButton deploymentID={deployment.id} stageIdentifier={stage || 'development'} css={switchStyle} />
      </header>
      <section>
        <Input label={primaryHttpHostName} value={primaryHttpHostUrl} openLinkInNewTab={primaryHttpHostUrl} />
        <div css={connectStyle}>
          <Input label="Connect via SSH" value={sshConnectString} canCopy />
          {/* <button data-url={createVSCodeUri(sshUrl, sshEditorPath)} onClick={onMenu}>
            <DotsThreeVertical size={24} />
          </button> */}
        </div>
        <Spectrum deploymentID={deployment.id} label="Status" slices={74} className="spectrum MuiFormControl-root" />
      </section>

      <Button
        loading={pinMutation.isLoading}
        icon={<PushPin weight={pin ? 'fill' : 'bold'} />}
        primary={pin}
        data-pinned={pin}
        small
        className="pin"
        css={pinButtonStyle}
        onClick={handlePin}
      >
        {pin ? index + 1 : 'Pin'}
      </Button>
    </article>
  );
};

const containerStyle = (theme: Theme) => css`
  position: relative;
  margin-bottom: 48px;
  max-width: 65ch;

  &:hover {
    > .pin {
      transform: scale(1);
      opacity: 1;
    }
  }

  > header {
    display: flex;
    align-items: center;
    margin-bottom: 2px;
    background-color: ${theme.color.grey[60]};
    border-radius: ${borderRadius}px ${borderRadius}px 0 0;
    overflow: hidden;
  }

  > section {
    border-radius: 0 0 ${borderRadius}px ${borderRadius}px;
    overflow: hidden;
  }

  .grid,
  > section {
    > a {
      margin: 0;
    }

    .input,
    .spectrum {
      margin: 0;
      margin-top: -2px;
      height: 80px;

      > label {
        top: 10px;
      }

      > input,
      &.spectrum {
        border-radius: 0px;
        border: 2px solid ${theme.color.background};
        border-left: none;
        border-right: none;
      }

      &.spectrum {
        padding-top: 10px;
        padding-bottom: 8px;

        > div {
          height: 34px;
        }
      }

      &:last-of-type {
        border-bottom: none;
      }

      > .MuiFormHelperText-root {
        display: none;
      }
    }
  }

  > footer {
    display: flex;
    align-items: center;

    > div:first-of-type {
      flex: 1;
      display: flex;
      align-items: center;
    }

    > div:last-of-type {
      display: flex;
      justify-content: flex-end;
    }

    .MuiAvatarGroup-root {
      margin-right: 0.5rem;
    }

    .MuiAvatar-root {
      width: 32px;
      height: 32px;
      font-size: 0.75rem;
      background-color: ${theme.color.primary[60]};
      color: ${theme.color.primary.text};
    }
  }
`;

const titleStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  position: relative;
  flex: 1;
  padding: 16px;
  color: inherit;
  text-decoration: none;
  user-select: none;

  transition: background-color ${Transition.basic};

  &:hover {
    background-color: ${applyEffect(theme.color.grey[60], 'hover')};

    > svg {
      color: ${theme.color.foreground};
    }

    > figure .ornament > svg {
      color: ${applyEffect(theme.color.grey[60], 'hover')};
    }
  }

  > figure {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    margin-right: 0.8rem;

    .MuiListItemIcon-root {
      min-width: auto;
    }

    .ornament > svg {
      transition: color ${Transition.basic};
      color: ${theme.color.grey[60]};
    }
  }

  > div:first-of-type {
    flex: 1;
  }

  > svg {
    position: absolute;
    right: 16px;
    width: 24px;
    height: 24px;
    color: ${theme.color.grey[80]};

    transition: color ${Transition.basic};
  }
`;

const switchStyle = (theme: Theme) => css`
  align-self: stretch;
  padding-left: 0px;
  padding-right: 18px;
  min-width: 135px;
  border: none;
  border-left: 2px solid ${theme.color.background} !important;
  border-radius: 0;

  > span:first-of-type {
    font-weight: normal;
    font-size: 0.875rem;
    font-family: ${Font.body};
    color: ${theme.color.grey[100]};
  }

  > .icon {
    margin-right: 0;
  }
`;

const textStyle = css`
  display: flex;
  align-items: center;
  position: relative;
  padding-left: calc(0.8rem + 4px + 3px);

  .MuiListItemText-root {
    margin: 0;
  }

  .MuiListItemText-primary {
    font-weight: bold;
  }

  .MuiListItemText-secondary {
    display: flex;
    align-items: center;

    > svg {
      margin-top: -1px;
      margin-right: 2px;
    }
  }
`;

const statusBarStyle = css`
  position: absolute;
  left: 0;
  width: 4px;
  height: 100%;
  border-radius: 4px;
`;

const connectStyle = (theme: Theme) => css`
  display: flex;
  height: 78px;

  > .MuiFormControl-root {
    margin: 0;
    padding-top: 10px;
    padding-bottom: 8px;
    height: 78px;

    > label {
      top: 11px;
    }
  }

  > button {
    padding: 0;
    background-color: ${theme.color.grey[60]};
    border: none;
    border-left: 2px solid ${theme.color.background};
    color: ${theme.color.grey[80]};
    cursor: pointer;

    transition: background-color ${Transition.basic};

    :hover {
      background-color: ${applyEffect(theme.color.grey[60], 'hover')};
      color: ${theme.color.foreground};
    }
  }
`;

const subtitleStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2px 0;

  > .body2 {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
`;

const sizeStyle = (theme: Theme) => css`
  background-color: ${theme.color.grey[80]};
  color: ${theme.color.foreground} !important;
  pointer-events: none;
`;

const pinButtonStyle = (theme: Theme) => css`
  position: absolute;
  top: -16px;
  left: -16px;
  border: 2px solid ${theme.color.background};
  transform: scale(0);
  opacity: 0;

  transition: opacity 200ms ease, transform 200ms ${theme.easing.springy};

  &:hover,
  &:active,
  &:focus,
  &[data-pinned='true'] {
    transform: scale(1);
    opacity: 1;
  }

  > span {
    margin-left: 12px;
  }

  > .icon {
    transform: translateY(1.5px);
  }
`;
